// Plik: www/js/filterPills.js

/**
 * Escapes pill text (field label, typed value, option text) for the pill markup.
 * Same rules as _select2SafeText in the Select2 templates: character references and the
 * attribute-less formatting tags b, strong, i, em, u, small and br are kept, everything else is text.
 */
function escapePillText(text) {
    if (text === undefined || text === null) return '';
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(text).replace(/<(\/?)(b|strong|i|em|u|small|br)\s*\/?>|&(?![a-z][a-z0-9]*;|#[0-9]+;|#x[0-9a-f]+;)|[<>"']/gi,
        (m, slash, tag) => (tag ? '<' + slash + tag.toLowerCase() + '>' : map[m]));
}

export class FilterPills {
    constructor() {
        document.addEventListener('DOMContentLoaded', () => this.init());
    }

    init() {
        if (typeof window.__i18n !== 'function') {
            console.error('Funkcja i18n nie została znaleziona. FilterPills nie zostanie zainicjowany.');
            return;
        }

        document.querySelectorAll('form[data-dcms-filter-pills="true"]').forEach(form => {
            this.generatePills(form);
            form.addEventListener('click', (e) => this.handleClick(e, form));
            form.addEventListener('dcms:filter-updated', () => this.generatePills(form));
        });
    }

    generatePills(form) {
        const container = form.querySelector('.filter-pills-container');
        if (!container) return;

        let pillsHtml = '';
        const processedFields = new Set(); // Stores the `name` of already processed fields

        // STEP 1: Process complex components (like DateTimePicker)
        form.querySelectorAll('[data-dcms-field-id]').forEach(componentContainer => {
            const componentId = componentContainer.dataset.dcmsFieldId;
            const pillFunctionName = `${componentId}_generatePillData`;

            if (typeof window[pillFunctionName] === 'function') {
                const pillData = window[pillFunctionName]();
                if (pillData) {
                    pillsHtml += this.createPillHtml(pillData.label, pillData.displayValue, componentId);
                    // Mark all fields inside this component as processed
                    componentContainer.querySelectorAll('[name]').forEach(field => processedFields.add(field.name));
                }
            }
        });

        // STEP 2: Process the remaining, simple fields
        form.querySelectorAll('input:not([type="hidden"]), select, textarea').forEach(element => {
            if (!element.name || processedFields.has(element.name) || element.type === 'submit' || element.type === 'button') {
                return;
            }

            const value = element.value;
            if (value !== null && value !== '' && value !== '0') {
                const labelElement = form.querySelector(`label[for="${element.id}"]`);
                const label = labelElement ? labelElement.textContent.trim().replace(/[*:]/g, '') : element.name;
                let displayValue = value;

                if (element.tagName === 'SELECT') {
                    const selectedOption = Array.from(element.options).find(opt => opt.value === value);
                    if (selectedOption) displayValue = selectedOption.text;
                }
                
                pillsHtml += this.createPillHtml(label, displayValue, element.id);
            }
        });
        
        // Aktualizacja widoku
        if (pillsHtml) {
            const title = window.__i18n ? __i18n('selectedFilters', 'commonjs', 'Wybrane filtry:') : 'Wybrane filtry:';
            const resetAllText = window.__i18n ? __i18n('resetAll', 'commonjs', 'wyczyść filtry') : 'wyczyść filtry';
            const titleWithReset = `<h4 class="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">${title} <button type="button" class="reset-all-filters text-xs font-normal text-blue-600 hover:underline ml-2">(${resetAllText})</button></h4>`;
            container.innerHTML = `${titleWithReset}<div class="flex flex-wrap gap-2">${pillsHtml}</div>`;
            container.style.display = 'block';
        } else {
            container.innerHTML = '';
            container.style.display = 'none';
        }
    }

    createPillHtml(label, displayValue, fieldId) {
        label = escapePillText(label);
        displayValue = escapePillText(displayValue);
        fieldId = escapePillText(fieldId);
        return `
            <div class="inline-flex items-center bg-gray-200 text-gray-800 text-xs font-medium px-2.5 py-1 rounded-full dark:bg-gray-700 dark:text-gray-300">
                <span><strong>${label}:</strong> ${displayValue}</span>
                <button type="button" class="remove-pill cursor-pointer ml-2 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white" title="Usuń filtr" data-field-id="${fieldId}">
                    <span class="material-symbols-rounded text-sm pointer-events-none">cancel</span>
                </button>
            </div>
        `;
    }

    handleClick(event, form) {
        const removeButton = event.target.closest('.remove-pill');
        const resetButton = event.target.closest('.reset-all-filters');

        if (removeButton) {
            const fieldId = removeButton.dataset.fieldId;
            this.resetFieldValue(form, fieldId);
            this.submitForm(form);
        } else if (resetButton) {
            event.preventDefault();
            this.handleResetAll(form);
        }
    }
    
    handleResetAll(form) {
        form.querySelectorAll('[data-dcms-field-id], input:not([type="hidden"]), select').forEach(element => {
            this.resetFieldValue(form, element.id);
        });
        this.submitForm(form);
    }

    resetFieldValue(form, fieldId) {
        const resetFunctionName = `${fieldId}_resetFilterValue`;
        
        if (typeof window[resetFunctionName] === 'function') {
            window[resetFunctionName]();
        } else {
            const field = form.querySelector(`#${fieldId}`);
            if (field) {
                if (field.type === 'checkbox' || field.type === 'radio') {
                    field.checked = false;
                } else {
                    field.value = '';
                }
                if (field.tagName === 'SELECT' && $(field).data('select2')) {
                    $(field).val(null).trigger('change.select2');
                }
            }
        }
    }

    submitForm(form) {
        const submitButton = form.querySelector('#dcms-form-submit-button') || form.querySelector('[type="submit"]');
        if (submitButton) {
            submitButton.click();
        } else {
            form.requestSubmit();
        }
    }
}